import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, TextField, Paper, Divider, Select, MenuItem, FormControl, InputLabel,
  Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Snackbar, Alert, IconButton, Tooltip, Popover,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateCalendar, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminFilterPanel from "../../components/admin/AdminFilterPanel";
import { listIntegrationLogs } from "../../services/adminService";
import { ADMIN_PAPER_SX, ADMIN_GRID_SX } from "../../components/admin/adminGridStyles";
import "../../components/admin/adminFilter.css";

/* ── date range helpers ── */
const formatBr  = (d) => d ? dayjs(d).format("DD/MM/YYYY") : "";
const toIso     = (d) => d ? dayjs(d).format("YYYY-MM-DD") : "";
const isSameDay = (a, b) => a && b && dayjs(a).isSame(dayjs(b), "day");
const isBetween = (day, s, e) =>
  s && e && dayjs(day).isAfter(dayjs(s), "day") && dayjs(day).isBefore(dayjs(e), "day");

function RangeDay({ day, startDate, endDate, ...other }) {
  const isStart   = isSameDay(day, startDate);
  const isEnd     = isSameDay(day, endDate);
  const isInRange = isBetween(day, startDate, endDate);
  return (
    <PickersDay {...other} day={day} selected={false} sx={{
      borderRadius: isStart || isEnd ? "50%" : 0,
      backgroundColor: isStart || isEnd ? "primary.main" : isInRange ? "primary.light" : undefined,
      color: isStart || isEnd ? "primary.contrastText" : undefined,
    }} />
  );
}

/* ── status chip ── */
const STATUS_COLORS = {
  sucesso: { color: "#15803d", bg: "#dcfce7" }, success: { color: "#15803d", bg: "#dcfce7" },
  erro:    { color: "#b91c1c", bg: "#fee2e2" }, error:   { color: "#b91c1c", bg: "#fee2e2" },
};
const statusChip = (value) => {
  const s = STATUS_COLORS[(value || "").toLowerCase()] || { color: "#374151", bg: "#f3f4f6" };
  const label = value === "success" ? "Sucesso" : value === "error" ? "Erro" : value || "—";
  return (
    <Chip label={label} size="small"
      sx={{ fontSize: 11, fontWeight: 600, color: s.color, backgroundColor: s.bg, borderRadius: 1, height: 20 }} />
  );
};

const TIPOS       = ["", "SEFAZ_DIST", "SAP_PI"];
const TIPO_LABELS = { "": "", SEFAZ_DIST: "SEFAZ", SAP_PI: "SAP PI" };
const STATUS_OPTS = ["", "success", "error"];
const STATUS_LABELS = { "": "", success: "Sucesso", error: "Erro" };

const EMPTY_FILTERS = { tipo: "", dateRange: "", dateFrom: "", dateTo: "", cnpj: "", status: "" };

function PayloadBlock({ label, value }) {
  let formatted = value;
  if (value && typeof value === "object") {
    try { formatted = JSON.stringify(value, null, 2); } catch { formatted = String(value); }
  } else if (typeof value === "string") {
    try { formatted = JSON.stringify(JSON.parse(value), null, 2); } catch { /* keep raw */ }
  }
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
        {label}
      </Typography>
      <Box component="pre" sx={{
        m: 0, p: 1.5, borderRadius: 1, backgroundColor: "background.subtle",
        border: "1px solid", borderColor: "divider", fontSize: 11, fontFamily: "monospace",
        whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 260, overflowY: "auto", color: "text.primary",
      }}>
        {formatted || "—"}
      </Box>
    </Box>
  );
}

export default function IntegrationLogPage() {
  const { t } = useTranslation();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [detail, setDetail]   = useState(null);
  const [snack, setSnack]     = useState({ open: false, msg: "", severity: "error" });

  /* date range state */
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [rangeStart, setRangeStart]         = useState(null);
  const [rangeEnd, setRangeEnd]             = useState(null);
  const [selectingEnd, setSelectingEnd]     = useState(false);

  const showSnack = (msg, severity = "error") => setSnack({ open: true, msg, severity });

  const load = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.tipo)     params.integrationType = f.tipo;
      if (f.dateFrom) params.dateFrom        = f.dateFrom;
      if (f.dateTo)   params.dateTo          = f.dateTo;
      if (f.cnpj)     params.cnpj            = f.cnpj.trim();
      if (f.status)   params.status          = f.status;
      const data = await listIntegrationLogs(params);
      setRows(Array.isArray(data) ? data : (data?.items ?? []));
    } catch (e) { showSnack(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(EMPTY_FILTERS); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = () => load(filters);

  const sf = (field) => ({
    value: filters[field],
    onChange: (e) => setFilters((p) => ({ ...p, [field]: e.target.value })),
  });

  /* date range handlers */
  const handleCalendarChange = (newDate) => {
    if (!selectingEnd || !rangeStart) {
      setRangeStart(newDate); setRangeEnd(newDate); setSelectingEnd(true);
      setFilters((p) => ({ ...p, dateRange: formatBr(newDate), dateFrom: toIso(newDate), dateTo: toIso(newDate) }));
      return;
    }
    let start = rangeStart, end = newDate;
    if (dayjs(end).isBefore(dayjs(start), "day")) [start, end] = [end, start];
    setRangeStart(start); setRangeEnd(end); setSelectingEnd(false);
    const same = dayjs(start).isSame(dayjs(end), "day");
    setFilters((p) => ({
      ...p,
      dateRange: same ? formatBr(start) : `${formatBr(start)} - ${formatBr(end)}`,
      dateFrom:  toIso(start),
      dateTo:    toIso(end),
    }));
    setCalendarAnchor(null);
  };

  const clearDate = () => {
    setRangeStart(null); setRangeEnd(null); setSelectingEnd(false);
    setFilters((p) => ({ ...p, dateRange: "", dateFrom: "", dateTo: "" }));
    setCalendarAnchor(null);
  };

  const columns = [
    {
      field: "executedAt", headerName: t("admin.col.dataHora"), width: 160,
      valueFormatter: (v) => { try { return v ? new Date(v).toLocaleString("pt-BR") : "—"; } catch { return v; } },
    },
    {
      field: "integrationType", headerName: t("admin.col.tipo"), width: 130,
      valueFormatter: (v) => TIPO_LABELS[v] ?? v,
    },
    { field: "cnpj", headerName: t("admin.col.cnpj"), width: 180 },
    { field: "status", headerName: t("admin.filter.status"), width: 110, renderCell: ({ value }) => statusChip(value) },
    {
      field: "requestPayload", headerName: t("admin.col.payloadEnvio"), flex: 1,
      renderCell: ({ value }) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value ? JSON.stringify(value) : "—"}
        </Typography>
      ),
    },
    {
      field: "responsePayload", headerName: t("admin.col.payloadRetorno"), flex: 1,
      renderCell: ({ value }) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value ? JSON.stringify(value) : "—"}
        </Typography>
      ),
    },
    {
      field: "__detail", headerName: "", width: 48, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title={t("admin.buttons.verDetalhes")}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetail(row); }}>
            <OpenInNewIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <AdminPageHeader title={t("admin.pages.integrationLog")} />

      <AdminFilterPanel>
        {/* Tipo */}
        <FormControl size="small" className="compactField" sx={{ minWidth: 160 }}>
          <InputLabel shrink>{t("admin.filter.tipoIntegracao")}</InputLabel>
          <Select {...sf("tipo")} label={t("admin.filter.tipoIntegracao")} notched displayEmpty
            MenuProps={{ PaperProps: { className: "docTypeMenu" } }}>
            {TIPOS.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {TIPO_LABELS[tipo] || <em>{t("admin.label.todos")}</em>}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Data range */}
        <TextField
          label={t("admin.filter.data")}
          value={filters.dateRange || ""}
          size="small"
          className="compactField"
          InputLabelProps={{ shrink: true }}
          inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
          onClick={(e) => setCalendarAnchor(e.currentTarget)}
          sx={{ width: 170 }}
        />
        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{ className: "issueDatePopover" }}
        >
          <Box sx={{ p: 1, pb: 0 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={rangeEnd || rangeStart || dayjs()}
                onChange={handleCalendarChange}
                slots={{ day: RangeDay }}
                slotProps={{ day: { startDate: rangeStart, endDate: rangeEnd } }}
              />
            </LocalizationProvider>
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button size="small" onClick={clearDate} sx={{ textTransform: "none" }}>
                {t("admin.buttons.limpar")}
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* CNPJ */}
        <TextField label={t("admin.filter.cnpj")} size="small" className="compactField" {...sf("cnpj")}
          InputLabelProps={{ shrink: true }} sx={{ width: 180 }} inputProps={{ maxLength: 14 }} />

        {/* Status */}
        <FormControl size="small" className="compactField" sx={{ minWidth: 130 }}>
          <InputLabel shrink>{t("admin.filter.status")}</InputLabel>
          <Select {...sf("status")} label={t("admin.filter.status")} notched displayEmpty
            MenuProps={{ PaperProps: { className: "docTypeMenu" } }}>
            {STATUS_OPTS.map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_LABELS[s] || <em>{t("admin.label.todos")}</em>}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Buscar */}
        <Button
          type="button"
          variant="contained"
          className="btnBuscar compactField"
          onClick={handleFilter}
          disabled={loading}
          sx={{ width: "auto !important", minWidth: "80px !important", flexShrink: 0 }}
        >
          {t("admin.buttons.buscar")}
        </Button>
      </AdminFilterPanel>

      <Paper sx={ADMIN_PAPER_SX}>
        <DataGrid
          rows={rows} columns={columns} getRowId={(r) => r.id}
          loading={loading} autoHeight disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          onRowClick={({ row }) => setDetail(row)}
          sx={ADMIN_GRID_SX}
        />
      </Paper>

      {/* Dialog de detalhes */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          {t("admin.dialog.callDetails")}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {detail && (
            <>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>{t("admin.detail.dataHora")}</Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {detail.executedAt ? new Date(detail.executedAt).toLocaleString("pt-BR") : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>{t("admin.detail.tipo")}</Typography>
                  <Typography sx={{ fontSize: 13 }}>{TIPO_LABELS[detail.integrationType] ?? detail.integrationType ?? "—"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>{t("admin.detail.cnpj")}</Typography>
                  <Typography sx={{ fontSize: 13 }}>{detail.cnpj || "—"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>{t("admin.detail.status")}</Typography>
                  <Box sx={{ mt: 0.3 }}>{statusChip(detail.status)}</Box>
                </Box>
              </Box>
              <Divider />
              <PayloadBlock label={t("admin.col.payloadEnvio")}   value={detail.requestPayload} />
              <PayloadBlock label={t("admin.col.payloadRetorno")} value={detail.responsePayload} />
            </>
          )}
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2 }}>
          <Button onClick={() => setDetail(null)} sx={{ textTransform: "none", fontSize: 13 }}>
            {t("admin.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
